/**
 * Fetch wrapper for the TrueReel API: credentials included so the
 * `truereel_session` cookie travels (docs/07 §0), the error envelope
 * `{"error": {"code", "message", "detail"}, "request_id"}` (docs/07 §3) turned
 * into an `ApiError`, and an optional `Idempotency-Key` on writes.
 */

export const API_URL = (
  import.meta.env.VITE_API_URL ?? "http://127.0.0.1:8000"
).replace(/\/$/, "");

/** The Accounts web app, for top-up, receipts and the account page. */
export const ACCOUNTS_WEB_URL = (
  import.meta.env.VITE_ACCOUNTS_WEB_URL ?? "http://localhost:5174"
).replace(/\/$/, "");

export interface ErrorEnvelope {
  error: { code: string; message: string; detail?: unknown };
  request_id?: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly detail: unknown = null,
    public readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Sent as the `Idempotency-Key` header. Every write accepts one (docs/07
   * §0); pass the same key on a retry to get the original response back
   * instead of a second job or a second charge.
   */
  idempotencyKey?: string;
  /** Told the status of a successful answer, for a call whose 201 and 202
   *  mean different things (restoring a step of a design). */
  onStatus?: (status: number) => void;
}

/** A metered route's 202 `{estimate, kind, confirm: "required"}` (§15.1,
 *  P28-T07): nothing was started; the action waits to be sent again with
 *  `confirm: true` once the person said yes to its estimate (P28-T08). */
export function needsConfirm(answer: unknown): boolean {
  return (
    typeof answer === "object" &&
    answer !== null &&
    (answer as { confirm?: unknown }).confirm === "required"
  );
}

/** A metered route's 202 when it waits for the person's yes (§15.1). */
export interface PricedAnswer {
  estimate: number;
  kind: string;
  confirm: "required";
}

/** The estimate a route asks a yes for. */
export interface PriceAsk {
  estimate: number;
  kind: string;
}

/** The 202's estimate when the answer asks for a yes, else null. */
export function priceAsk(answer: unknown): PriceAsk | null {
  if (!needsConfirm(answer)) return null;
  const { estimate, kind } = answer as { estimate?: unknown; kind?: unknown };
  return {
    estimate: typeof estimate === "number" ? estimate : 0,
    kind: typeof kind === "string" ? kind : "",
  };
}

/** What a 402 `insufficient_credits` says (§15.1): the estimate, what is
 *  available and Accounts' top-up page (null when the route did not say). */
export interface Shortfall {
  needed: number | null;
  available: number | null;
  top_up_url: string;
}

/** The shortfall of an action refused for credits, else null. */
export function shortfallOf(error: unknown): Shortfall | null {
  if (!(error instanceof ApiError) || error.code !== "insufficient_credits") {
    return null;
  }
  const detail = (error.detail ?? {}) as {
    needed?: unknown;
    available?: unknown;
    top_up_url?: unknown;
  };
  return {
    needed: typeof detail.needed === "number" ? detail.needed : null,
    available: typeof detail.available === "number" ? detail.available : null,
    top_up_url:
      typeof detail.top_up_url === "string" && detail.top_up_url
        ? detail.top_up_url
        : `${ACCOUNTS_WEB_URL}/account`,
  };
}

/** A fresh key for one logical write; keep it across retries of that write. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function parseBody(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // A proxy error page or an HTML 502 — not our envelope.
    return null;
  }
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  if (options.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }
  const response = await fetch(API_URL + path, {
    method,
    headers,
    body,
    credentials: "include",
    signal: options.signal,
  });
  if (response.ok) options.onStatus?.(response.status);
  if (response.status === 204) return undefined as T;
  const data = parseBody(await response.text());
  if (!response.ok) {
    const envelope = data as Partial<ErrorEnvelope> | null;
    const err = envelope?.error;
    throw new ApiError(
      response.status,
      err?.code ?? "http_error",
      err?.message ?? response.statusText,
      err?.detail ?? null,
      envelope?.request_id ?? response.headers.get("X-Request-ID"),
    );
  }
  return data as T;
}

/** Like `api`, for an endpoint that answers bytes (the studio's `audio/wav`
 *  preview): the same cookie, headers and error envelope, the body as a Blob. */
export async function apiBlob(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = { ...options.headers };
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  if (options.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }
  const response = await fetch(API_URL + path, {
    method,
    headers,
    body,
    credentials: "include",
    signal: options.signal,
  });
  if (!response.ok) {
    const envelope = parseBody(
      await response.text(),
    ) as Partial<ErrorEnvelope> | null;
    const err = envelope?.error;
    throw new ApiError(
      response.status,
      err?.code ?? "http_error",
      err?.message ?? response.statusText,
      err?.detail ?? null,
      envelope?.request_id ?? response.headers.get("X-Request-ID"),
    );
  }
  return response.blob();
}

/** A multipart POST. No Content-Type header (the browser adds it with the
 *  boundary) and no Idempotency-Key. Same cookie and error envelope as `api`. */
export async function apiForm<T>(path: string, formData: FormData): Promise<T> {
  const response = await fetch(API_URL + path, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: formData,
    credentials: "include",
  });
  if (response.status === 204) return undefined as T;
  const data = parseBody(await response.text());
  if (!response.ok) {
    const envelope = data as Partial<ErrorEnvelope> | null;
    const err = envelope?.error;
    throw new ApiError(
      response.status,
      err?.code ?? "http_error",
      err?.message ?? response.statusText,
      err?.detail ?? null,
      envelope?.request_id ?? response.headers.get("X-Request-ID"),
    );
  }
  return data as T;
}
